// Всплывающая подсказка: через 0,35 с при наведении и сразу при фокусе с клавиатуры (README «Иконки»).
// Фокус, который поставила программа (открытие окна, панели, меню, возврат фокуса после закрытия
// слоя), подсказку не открывает (044, MOT-13, MOT-14): иначе «Закрыть Esc» всплывала над окном,
// а «История пункта» — над таблицей после Esc. Подсказка — не слой для Esc: нажатие при ней
// закрывает и её, и то, что Esc закрыл бы без неё (MOT-16).
// Подсказка не шире 360 px, текст в ней переносится по словам; текстовая подсказка обрывается
// многоточием после `lines` строк и не закрывает экран (правка 0.27.3, kp.css «Подсказка»).
// Клавиша действия — вне предела строк, её видно всегда. Составная подсказка (список людей
// доступа, «Найти или перейти ⌘K · поиск /») — как была: её строки задаёт сам экран.
import * as RadixTooltip from '@radix-ui/react-tooltip';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, FocusEvent, ReactElement, ReactNode } from 'react';
import { focusFromNavigation } from '../lib/focus-source.ts';
import { relayEscape } from '../lib/layers.ts';
import { ShortcutKeys } from './Kbd.tsx';
import type { Shortcut } from './Kbd.tsx';

export const TOOLTIP_DELAY_MS = 350;

/** Строк текста в подсказке по умолчанию; дальше — многоточие. */
export const TOOLTIP_MAX_LINES = 6;

/** Отступ подсказки от края окна: у края она сдвигается внутрь. */
const TOOLTIP_COLLISION_PADDING = 8;

export interface TooltipProps {
  /** Текст подсказки. */
  content: ReactNode;
  /** Клавиша действия: «Скрыть меню [», «Главная G затем H». */
  shortcut?: Shortcut;
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Сколько строк текста-строки видно; дальше — многоточие. По умолчанию TOOLTIP_MAX_LINES. */
  lines?: number;
  /** Элемент, к которому относится подсказка; должен принимать ref и обработчики. */
  children: ReactElement;
  /** Не показывать (например, значение не сокращено). Разметка остаётся той же. */
  disabled?: boolean;
  /**
   * Задержка при фокусе с клавиатуры, мс; по умолчанию 0 — сразу. Пункт меню с пояснением ждёт
   * те же 0,35 с, что и при наведении: проход стрелками по меню не показывает подсказку у каждого
   * пункта (044, research Р9).
   */
  focusDelay?: number;
  /** Управляемая подсказка: пункт меню открывает причину недоступности по нажатию. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/** Предел строк для класса kp-clamp (kp.css). */
export function linesStyle(lines: number): CSSProperties {
  return { '--kp-lines': lines } as CSSProperties;
}

export function Tooltip({
  content,
  shortcut,
  side = 'bottom',
  lines = TOOLTIP_MAX_LINES,
  children,
  disabled,
  focusDelay = 0,
  open: openProp,
  onOpenChange,
}: TooltipProps) {
  const [ownOpen, setOwnOpen] = useState(false);
  const open = openProp ?? ownOpen;
  const controlled = openProp !== undefined;
  const setOpen = useCallback(
    (next: boolean) => {
      if (!controlled) setOwnOpen(next);
      onOpenChange?.(next);
    },
    [controlled, onOpenChange],
  );
  const contentRef = useRef<HTMLDivElement>(null);
  const focusTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(focusTimer.current), []);

  // Отменённое событие фокуса Radix не открывает (composeEventHandlers): так подсказку не видит
  // фокус кодом, а фокус с клавиатуры при focusDelay открывает её по таймеру.
  const onFocus = (event: FocusEvent<HTMLElement>) => {
    window.clearTimeout(focusTimer.current);
    if (!focusFromNavigation()) {
      event.preventDefault();
      return;
    }
    if (focusDelay > 0) {
      event.preventDefault();
      focusTimer.current = window.setTimeout(() => setOpen(true), focusDelay);
    }
  };
  const onBlur = () => window.clearTimeout(focusTimer.current);

  return (
    <RadixTooltip.Provider delayDuration={TOOLTIP_DELAY_MS} skipDelayDuration={200}>
      <RadixTooltip.Root open={!disabled && open} onOpenChange={setOpen}>
        <RadixTooltip.Trigger asChild onFocus={onFocus} onBlur={onBlur}>
          {children}
        </RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content
            ref={contentRef}
            side={side}
            sideOffset={6}
            collisionPadding={TOOLTIP_COLLISION_PADDING}
            // Esc снимает подсказку сразу, без выхода, и уходит тому, кто в фокусе: окну, меню,
            // странице (research Р4).
            onEscapeKeyDown={(event) => relayEscape(contentRef.current, event)}
            className="t-tip-box kp-tip"
          >
            {typeof content === 'string' ? (
              <span className="kp-tip-text kp-clamp" style={linesStyle(lines)}>
                {content}
              </span>
            ) : (
              content
            )}
            {shortcut ? (
              <>
                {' '}
                <ShortcutKeys shortcut={shortcut} />
              </>
            ) : null}
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
}
